import type { InterviewSectionDefinition } from "@repo/app-types/entities/interviewSection/definition";
import type { InterviewSectionSettings } from "@repo/app-types/entities/interviewSection/settings";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { interviewSectionDefinitionApi } from "../../../api/entities/interviewSectionDefinition.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  type ChangedInterviewSectionDefinition,
  insertInterviewSectionDefinitionChange,
} from "./state.js";

export function useInterviewSectionDefinitionsStore(
  jobTypeId: string | undefined,
) {
  const [changedSections, setChangedSections] = useState<
    readonly ChangedInterviewSectionDefinition[]
  >([]);

  const onSectionCreate = (settings: InterviewSectionSettings) => {
    const id = `client:${uuidv4()}`;
    setChangedSections((prev) =>
      insertInterviewSectionDefinitionChange(prev, {
        changeType: "created",
        id,
        settings,
      }),
    );
    return id;
  };

  const onSectionUpdate = (id: string, settings: InterviewSectionSettings) => {
    setChangedSections((prev) =>
      insertInterviewSectionDefinitionChange(prev, {
        changeType: "updated",
        id,
        settings,
      }),
    );
  };

  const onSectionDelete = (id: string) => {
    setChangedSections((prev) =>
      insertInterviewSectionDefinitionChange(prev, {
        changeType: "deleted",
        id,
      }),
    );
  };

  const dispatch = useAppDispatch();
  const onSectionsSave = async () => {
    if (!jobTypeId) {
      return {};
    }

    // TODO: Handle errors.
    const results = await Promise.all(
      changedSections.map(
        async (
          changedSection,
        ): Promise<[string, InterviewSectionDefinition | undefined]> => {
          switch (changedSection.changeType) {
            case "created": {
              const savedSection = await dispatch(
                interviewSectionDefinitionApi.endpoints.createInterviewSectionDefinition.initiate(
                  {
                    jobTypeId,
                    settings: changedSection.settings,
                  },
                ),
              ).unwrap();

              return [changedSection.id, savedSection] as const;
            }
            case "updated": {
              const updatedSection = await dispatch(
                interviewSectionDefinitionApi.endpoints.updateInterviewSectionDefinition.initiate(
                  {
                    jobTypeId,
                    id: changedSection.id,
                    settings: changedSection.settings,
                  },
                ),
              ).unwrap();

              return [changedSection.id, updatedSection] as const;
            }
            case "deleted": {
              await dispatch(
                interviewSectionDefinitionApi.endpoints.deleteInterviewSectionDefinition.initiate(
                  {
                    jobTypeId,
                    id: changedSection.id,
                  },
                ),
              ).unwrap();

              return [changedSection.id, undefined] as const;
            }
          }
        },
      ),
    );

    return Object.fromEntries(results) as Record<
      string,
      InterviewSectionDefinition | undefined
    >;
  };

  const onSectionsReset = () => {
    setChangedSections([]);
  };

  const hasMadeChanges = changedSections.length > 0;

  return {
    changedSections,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onSectionsSave,
    onSectionsReset,
    hasMadeChanges,
  };
}
